/**
 * The banks offered in the dropdown when somebody records where they are paid.
 *
 * Chosen by the owner on 21 September 2026 from a longer list, and kept short on purpose: this is
 * a phone dropdown that a delivery worker uses once, and forty entries to scroll through is worse
 * than nineteen and an "Other". Anything not here is typed in, which is what every account was
 * before this existed.
 *
 * The order is the owner's and is deliberate — the public sector banks, then the private ones —
 * because that is how people here name them. It is not alphabetical and should not be sorted.
 *
 * Karnataka Bank and Canara Bank are in it because the office is in Bengaluru and both are local.
 *
 * Free text is still accepted by the API: the dropdown is a convenience, not a constraint, and a
 * cooperative or regional bank must never be un-enterable because it missed this list.
 */
export const INDIAN_BANKS = [
  'State Bank of India',
  'Punjab National Bank',
  'Bank of Baroda',
  'Canara Bank',
  'Union Bank of India',
  'Bank of India',
  'Indian Bank',
  'Central Bank of India',
  'HDFC Bank',
  'ICICI Bank',
  'Axis Bank',
  'Kotak Mahindra Bank',
  'IndusInd Bank',
  'IDFC First Bank',
  'Yes Bank',
  'Federal Bank',
  'Karnataka Bank',
  'Bandhan Bank',
  'IDBI Bank',
] as const;

export type IndianBank = (typeof INDIAN_BANKS)[number];

/** Whether a stored name is one of the listed banks, or something typed in. */
export const isListedBank = (name: string): boolean =>
  (INDIAN_BANKS as readonly string[]).includes(name);

/**
 * What the dropdown shows, given the name on file and whether Other has been picked.
 *
 * Pure, and here rather than inside the component, because the web app has no DOM test setup —
 * the behaviour worth pinning is this decision, not the markup around it. The case that matters
 * is a name already on file that is not in the list: it must select Other and stay in the box,
 * so that opening an old record and saving it cannot quietly change what is printed on a slip.
 */
export function bankFieldState(
  value: string,
  choseOther: boolean,
): { select: string; showOther: boolean } {
  const typedIn = value !== '' && !isListedBank(value);
  const showOther = choseOther || typedIn;
  return { showOther, select: showOther ? 'other' : value };
}

/**
 * The new state after somebody picks from the dropdown.
 *
 * Picking Other clears the box only when it held a listed bank. A name that was already typed in
 * survives, so choosing Other on a record that is already on Other is not a way to lose it.
 */
export function bankAfterPick(
  value: string,
  choice: string,
): { value: string; choseOther: boolean } {
  if (choice === 'other') return { value: isListedBank(value) ? '' : value, choseOther: true };
  return { value: choice, choseOther: false };
}
